import { ChangeEvent } from 'react';

import { Button, Checkbox, Input, Modal, ModalActions, ModalContent, Typography, wait } from '@braskai/ui-kit';
import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { FormControlLabel, Stack } from '@mui/material';
import { z } from 'zod';

import { Form, FormField } from '@/shared/ui/form/Form';
import { ModalHeaderDefault } from '@/shared/ui/modal';

const formSchema = z.object({
  comment: z.string(),
  options: z.array(z.string()),
});

type FormValues = z.infer<typeof formSchema>;

type FeedbackFormProps = {
  isOpen: boolean;
  options: string[];
  onClose: () => void;
  onSubmit: (data: FormValues) => void;
};

export const TtsFeedbackModal = ({ isOpen, options, onClose, onSubmit }: FeedbackFormProps) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      comment: '',
      options: [],
    },
    resolver: zodResolver(formSchema),
  });

  const {
    control,
    formState: { isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = methods;

  const closeModal = async () => {
    onClose();
    await wait(200);
    reset();
  };

  return (
    <Modal open={isOpen} onClose={isSubmitting ? undefined : closeModal}>
      <ModalHeaderDefault title="What went wrong?" isCloseButtonDisabled={isSubmitting} onClose={closeModal} />
      <Form {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalContent>
            <Stack gap={20}>
              <Stack gap={8}>
                <FormField
                  name="options"
                  render={({ field }) => (
                    <>
                      {options.map((issue) => (
                        <FormControlLabel
                          key={issue}
                          label={
                            <Typography color="grey.700" variant="body-2-md">
                              {issue}
                            </Typography>
                          }
                          control={
                            <Checkbox
                              checked={field.value.includes(issue)}
                              disabled={isSubmitting}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(
                                  e.target.checked
                                    ? [...field.value, issue]
                                    : field.value.filter((value) => value !== issue),
                                );
                              }}
                            />
                          }
                          sx={(theme) => ({
                            border: `1px solid ${theme.palette.grey[200]}`,
                            borderRadius: 10,
                            gap: 8,
                            margin: 0,
                            padding: 10,
                          })}
                        />
                      ))}
                    </>
                  )}
                  control={control}
                />
              </Stack>
              <FormField
                name="comment"
                render={({ field }) => (
                  <Input
                    disabled={isSubmitting}
                    placeholder="Feedback here"
                    rows={3}
                    value={field.value}
                    multiline
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                )}
                control={control}
              />
            </Stack>
          </ModalContent>
          <ModalActions>
            <Button disabled={isSubmitting} size="lg" variant="outlined" fullWidth onClick={closeModal}>
              Cancel
            </Button>
            <Button disabled={isSubmitting || !isDirty} isLoading={isSubmitting} size="lg" type="submit" fullWidth>
              Submit
            </Button>
          </ModalActions>
        </form>
      </Form>
    </Modal>
  );
};
